/**
 * KnowledgePanel — chat-driven proposal of bedrock-knowledge SUGGESTIONS
 * attached to a KnowledgeBrief.
 *
 * Layout mirrors StructurePlanPanel: the working suggestion set on the
 * left grouped by target_scope → proposal_type, chat on the right. The
 * user reviews suggestions and applies them to the appropriate brief.
 * `target_scope='current'` lands on this scope's brief; 'category' /
 * 'project' promote up the hierarchy via the server's apply endpoint.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { QuickPromptBar } from './QuickPromptBar';
import { useWebSocket } from '../contexts/WebSocketContext';
import StageModelsWidget from './common/StageModelsWidget';
import Internals from './common/Internals';
import { AgentErrorBanner, AgentStatusChip } from './common/AgentErrorBanner';
import ChatMarkdown from './common/ChatMarkdown';
import ContextUsageChip from './ContextUsageChip';
import type {
  KnowledgeAgentScope,
  KnowledgePlanResponse,
  KnowledgeSuggestion,
  WebSocketEvent,
} from '../types';

interface Props {
  scope: KnowledgeAgentScope;
  scopeId: number;
  /** Scope name for the empty-state copy ("for this topic", etc.). */
  scopeName?: string;
}

// Display label + chip color per proposal type. Keys are LOWERCASE so
// we can `.toLowerCase()` whatever the API returns and look up directly
// — the backend serializes the enum's uppercase value, but the
// existing UI convention is to display lowercase.
const TYPE_META: Record<string, { label: string; cls: string }> = {
  heuristic: { label: 'Rule', cls: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' },
  misconception: { label: 'Misconception', cls: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' },
  expert_voice: { label: 'Expert view', cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' },
  contrarian: { label: 'Contested', cls: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' },
  fact: { label: 'Fact', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  terminology: { label: 'Term', cls: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300' },
  relationship: { label: 'Relationship', cls: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300' },
};

const TARGET_SCOPE_LABEL: Record<string, string> = {
  current: 'this scope',
  category: 'parent category',
  project: 'project',
};

// Order the target_scope groups appear in the left pane. Project at
// top because promoted items are the broadest signal; current at the
// bottom is the default place items land.
const TARGET_SCOPE_ORDER: Record<string, number> = {
  project: 0,
  category: 1,
  current: 2,
};

export default function KnowledgePanel({ scope, scopeId, scopeName }: Props) {
  const [plan, setPlan] = useState<KnowledgePlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Which window of static quick-prompts to show; the ↻ button rotates it.
  const [quickPromptPage, setQuickPromptPage] = useState(0);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const { subscribe } = useWebSocket();

  const refresh = async () => {
    try {
      const next = await api.getKnowledgePlan(scope, scopeId);
      setPlan(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load plan');
    }
  };

  // Initial load.
  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .getKnowledgePlan(scope, scopeId)
      .then((p) => {
        if (live) setPlan(p);
      })
      .catch((e) => {
        if (live) setError(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [scope, scopeId]);

  // Auto-scroll chat to the bottom whenever messages change or status flips.
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [plan?.messages?.length, plan?.status]);

  // Live agent lifecycle. Filter by scope so a project-level panel
  // ignores events for a sibling topic-scoped panel (both can be open).
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'knowledge_plan') return;
      if (event.scope !== scope || event.scope_id !== scopeId) return;
      if (event.status === 'replied' || event.status === 'failed') {
        refresh();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scopeId, subscribe]);

  const sendMessage = async (overrideText?: string) => {
    // Quick-prompt chips pass their text directly; the textarea path
    // falls back to the current draft.
    const text = (overrideText ?? chatInput).trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const next = await api.sendKnowledgePlanMessage(scope, scopeId, text);
      setPlan(next);
      setChatInput('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const deleteSuggestion = async (id: number) => {
    if (deletingId !== null) return;
    setDeletingId(id);
    try {
      const next = await api.deleteKnowledgeSuggestion(scope, scopeId, id);
      setPlan(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const applyAll = async () => {
    if (!plan || plan.suggestions.length === 0) return;
    const byScope = plan.suggestions.reduce<Record<string, number>>(
      (acc, s) => {
        acc[s.target_scope] = (acc[s.target_scope] || 0) + 1;
        return acc;
      },
      {},
    );
    const breakdown = Object.entries(byScope)
      .sort(
        (a, b) =>
          (TARGET_SCOPE_ORDER[a[0]] ?? 99) - (TARGET_SCOPE_ORDER[b[0]] ?? 99),
      )
      .map(([s, n]) => `${n} → ${TARGET_SCOPE_LABEL[s] ?? s}`)
      .join('\n');
    if (
      !window.confirm(
        `Apply ${plan.suggestions.length} suggestion${plan.suggestions.length === 1 ? '' : 's'}?\n\n` +
          breakdown +
          '\n\n' +
          '• Each suggestion becomes a real proposal on the matching brief.\n' +
          '• Duplicates (case-insensitive text match) are SKIPPED.\n' +
          '• Applied suggestions are removed from the working set.',
      )
    ) {
      return;
    }
    setApplying(true);
    setApplyResult(null);
    try {
      const result = await api.applyKnowledgePlan(scope, scopeId);
      const created = result.proposals_created;
      const skipped = result.skipped_duplicates;
      const breakdownStr = Object.entries(result.proposals_by_target_scope)
        .filter(([, n]) => n)
        .map(
          ([s, n]) => `${n} on ${TARGET_SCOPE_LABEL[s] ?? s}`,
        )
        .join(', ');
      const head = created
        ? `Applied ${created} suggestion${created === 1 ? '' : 's'}${
            breakdownStr ? ` (${breakdownStr})` : ''
          }.`
        : 'Nothing new to apply - every suggestion was already on the brief.';
      const tail = skipped
        ? ` ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped.`
        : '';
      setApplyResult(head + tail);
      setPlan(result.plan);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const startFreshChat = async () => {
    if (endingSession) return;
    if (
      !window.confirm(
        'Start a fresh chat?\n\n' +
          '• The current chat is hidden from the LLM and from this view.\n' +
          '• It stays saved in the database (no data is lost).\n' +
          '• The suggestion set on the left is preserved - keep iterating.',
      )
    ) {
      return;
    }
    setEndingSession(true);
    setError(null);
    try {
      const next = await api.endKnowledgePlanSession(scope, scopeId);
      setPlan(next);
      setApplyResult(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start fresh chat');
    } finally {
      setEndingSession(false);
    }
  };

  const resetPlan = async () => {
    if (resetting) return;
    if (
      !window.confirm(
        'Reset the planner?\n\n' +
          '• ALL chat history is wiped.\n' +
          '• ALL working suggestions are dropped.\n' +
          '• Committed proposals on the brief are NOT touched.\n\n' +
          'Use this for a clean-slate restart.',
      )
    ) {
      return;
    }
    setResetting(true);
    setError(null);
    try {
      const next = await api.resetKnowledgePlan(scope, scopeId);
      setPlan(next);
      setApplyResult(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  // Group suggestions by target_scope, then by proposal_type, for the
  // left pane. Sorted target groups first (project / category / current),
  // then alphabetically by type label inside each group.
  const groups = useMemo(() => {
    const items = plan?.suggestions ?? [];
    const byScope = new Map<string, KnowledgeSuggestion[]>();
    for (const s of items) {
      const k = s.target_scope || 'current';
      if (!byScope.has(k)) byScope.set(k, []);
      byScope.get(k)!.push(s);
    }
    return Array.from(byScope.entries())
      .sort(
        (a, b) =>
          (TARGET_SCOPE_ORDER[a[0]] ?? 99) - (TARGET_SCOPE_ORDER[b[0]] ?? 99),
      )
      .map(([scopeKey, list]) => {
        const byType = new Map<string, KnowledgeSuggestion[]>();
        for (const s of list) {
          const t = (s.proposal_type || 'fact').toLowerCase();
          if (!byType.has(t)) byType.set(t, []);
          byType.get(t)!.push(s);
        }
        const typeGroups = Array.from(byType.entries()).sort((a, b) =>
          (TYPE_META[a[0]]?.label ?? a[0]).localeCompare(
            TYPE_META[b[0]]?.label ?? b[0],
          ),
        );
        return { scopeKey, total: list.length, typeGroups };
      });
  }, [plan?.suggestions]);

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading planner…</div>;
  }
  if (error && !plan) {
    return (
      <div className="p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
        {error}
      </div>
    );
  }
  if (!plan) return null;

  const totalSuggestions = plan.suggestions.length;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Suggest knowledge
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Chat with the agent about bedrock-knowledge you want on this{' '}
          {scope}
          {scopeName ? <> &mdash; <em>{scopeName}</em></> : null}. Ask it to
          read URLs, search, or propose from its own knowledge. Review the
          list on the left, then <em>Apply</em> to commit them as real
          proposals on the matching brief.
        </p>
      </div>

      {error && (
        <div className="p-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded">
          {error}
        </div>
      )}
      {applyResult && (
        <div className="p-2 text-sm text-green-800 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded">
          {applyResult}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Suggestions pane */}
        <section className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800">
          <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-baseline justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Working suggestions
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {totalSuggestions} suggestion
                {totalSuggestions === 1 ? '' : 's'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={applyAll}
                disabled={applying || totalSuggestions === 0}
                className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50"
                title="Materialize every suggestion as a real KnowledgeProposal on the matching brief. Duplicates are skipped."
              >
                {applying ? 'Applying…' : 'Apply to brief'}
              </button>
              <button
                type="button"
                onClick={resetPlan}
                disabled={resetting}
                className="px-2 py-1.5 text-xs text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                title="Wipe ALL chat history and working suggestions. Committed proposals on the brief are not touched."
              >
                {resetting ? 'Resetting…' : 'Reset'}
              </button>
            </div>
          </header>
          <div className="p-3 max-h-[60vh] overflow-y-auto">
            {totalSuggestions === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                No suggestions yet. Send a chat message - e.g. "Propose 10
                bedrock heuristics for {scopeName || `this ${scope}`}" or
                paste a URL and say "read this and propose what you find."
              </div>
            ) : (
              <ol className="space-y-4">
                {groups.map((g) => (
                  <li key={g.scopeKey}>
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-600 dark:text-gray-300">
                        {TARGET_SCOPE_LABEL[g.scopeKey] ?? g.scopeKey}
                      </span>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        ({g.total})
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {g.typeGroups.map(([typeKey, items]) => (
                        <li key={typeKey}>
                          {items.map((s) => (
                            <SuggestionRow
                              key={s.id}
                              suggestion={s}
                              onDelete={() => deleteSuggestion(s.id)}
                              busy={deletingId === s.id}
                            />
                          ))}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>

        {/* Chat pane */}
        <section className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 flex flex-col">
          <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Chat
              {plan.current_session_number > 1 && (
                <span
                  className="ml-2 text-[11px] font-normal text-gray-500 dark:text-gray-400"
                  title="Earlier sessions are kept, but this conversation starts fresh from here."
                >
                  · session {plan.current_session_number}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <FreshChatButton
                disabled={
                  plan.status === 'thinking' ||
                  endingSession ||
                  plan.messages.length === 0
                }
                busy={endingSession}
                onClick={startFreshChat}
              />
              <ContextUsageChip
                contextTokens={plan.context_tokens}
                contextWindow={plan.context_window}
              />
              {/* Three stages bill behind this panel, not one: the planner,
                  the page reader it calls while researching, and the
                  compactor. The widget shows all three; the backend owns the
                  list (stage_surfaces."knowledge"). */}
              <StageModelsWidget surface="knowledge" />
              <AgentStatusChip status={plan.status} className="text-xs" />
            </div>
          </header>
          {plan.status === 'failed' && (
            <AgentErrorBanner error={plan.last_error} stage={plan.agent_stage} />
          )}
          <div className="flex-1 p-3 max-h-[55vh] overflow-y-auto space-y-2">
            {plan.messages.length === 0 ? (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                Ask the agent to propose bedrock-knowledge. Try "Propose 10
                heuristics a writer should follow for {scopeName || 'this scope'}"
                or paste a URL and say "read this and propose what you find."
              </div>
            ) : (
              plan.messages.map((m) => <ChatBubble key={m.id} message={m} />)
            )}
            {plan.status === 'thinking' && (
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span className="italic animate-pulse">AI is working…</span>
                <Internals>
                <button
                  type="button"
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Force-clear the "thinking" state? Use this if a previous request died. Auto-recovery normally kicks in after ~5 minutes.',
                      )
                    ) {
                      return;
                    }
                    try {
                      const next = await api.clearKnowledgePlanThinking(
                        scope,
                        scopeId,
                      );
                      setPlan(next);
                    } catch (e) {
                      setError(
                        e instanceof Error ? e.message : 'Failed to clear',
                      );
                    }
                  }}
                  className="text-indigo-600 dark:text-indigo-400 hover:underline not-italic"
                  title="Force-flip the chat back to idle if it's stuck."
                >
                  Stuck? Clear it
                </button>
                </Internals>
              </div>
            )}
            <div ref={chatBottomRef} />
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 p-2">
            <QuickPromptBar
              agentPrompts={plan.suggested_prompts}
              staticPool={staticKnowledgePrompts(
                plan.messages.length,
                plan.suggestions.length,
                scopeName,
              )}
              page={quickPromptPage}
              onCycle={() => setQuickPromptPage((p) => p + 1)}
              onSend={sendMessage}
              disabled={sending || plan.status === 'thinking'}
            />
            <textarea
              rows={3}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask the agent - paste a URL, request specific knowledge types, or describe what you need…"
              disabled={sending || plan.status === 'thinking'}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
            />
            <div className="flex items-center justify-between mt-1.5">
              <div className="text-[11px] text-gray-400 dark:text-gray-500">
                enter to send · shift+enter for newline
              </div>
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={
                  sending ||
                  plan.status === 'thinking' ||
                  !chatInput.trim()
                }
                className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {sending || plan.status === 'thinking' ? 'Working…' : 'Send'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------


function SuggestionRow({
  suggestion,
  onDelete,
  busy,
}: {
  suggestion: KnowledgeSuggestion;
  onDelete: () => void;
  busy?: boolean;
}) {
  const typeKey = (suggestion.proposal_type || 'fact').toLowerCase();
  const meta = TYPE_META[typeKey] ?? {
    label: suggestion.proposal_type,
    cls: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  };
  return (
    <div className="group flex items-start justify-between gap-2 py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline flex-wrap gap-1.5">
          <span
            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.cls}`}
          >
            {meta.label}
          </span>
          <span className="text-sm text-gray-900 dark:text-gray-100">{suggestion.text}</span>
        </div>
        {suggestion.source_urls.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {suggestion.source_urls.map((u, i) => (
              <a
                key={`${u}-${i}`}
                href={u}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline truncate max-w-[18rem]"
                title={u}
              >
                {hostname(u)}
              </a>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="icon-btn text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 px-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
        title="Drop this suggestion"
      >
        ×
      </button>
    </div>
  );
}


function FreshChatButton({
  disabled,
  busy,
  onClick,
}: {
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Start over: begins a fresh chat session. The current chat is hidden from the LLM (kept in the database). Working suggestions are preserved."
      className="px-2 py-0.5 text-[11px] rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
    >
      {busy ? 'Starting…' : 'Start over'}
    </button>
  );
}



function ChatBubble({
  message,
}: {
  message: {
    entity_type: string;
    content: string;
    has_tool_call: boolean;
    created_at: string;
  };
}) {
  // Case-insensitive — backend uppercases now, but be tolerant.
  const role = (message.entity_type || '').toUpperCase();

  if (role === 'SYSTEM') {
    return (
      <div className="my-1.5 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 rounded p-2.5 text-xs">
        <div className="flex items-baseline gap-2 mb-1 font-medium text-amber-800 dark:text-amber-300">
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 7h16M4 12h16M4 17h10"
            />
          </svg>
          <span>Compacted earlier conversation</span>
          <span
            className="text-amber-600 dark:text-amber-400 font-normal"
            title={new Date(message.created_at).toLocaleString()}
          >
            {formatChatTime(message.created_at)}
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words text-amber-900 dark:text-amber-200 italic">
          {message.content || '(empty summary)'}
        </div>
      </div>
    );
  }

  const isHuman = role === 'HUMAN';
  const bubbleClass = isHuman
    ? 'bg-indigo-600 text-white rounded-br-sm'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm';
  const labelClass = isHuman ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400';
  return (
    <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg p-2.5 text-sm shadow-sm ${bubbleClass}`}
      >
        <div
          className={`flex items-baseline gap-2 mb-1 text-[10px] font-medium ${labelClass}`}
        >
          <span>{isHuman ? 'You' : 'LLM'}</span>
          <span
            className="opacity-70"
            title={new Date(message.created_at).toLocaleString()}
          >
            {formatChatTime(message.created_at)}
          </span>
        </div>
        {/* The agent writes markdown, so render it. Raw, a reply naming your topic
            arrived as "**Age & Life Stage**" with the asterisks showing, and a
            bulleted list arrived as a run of hyphens. What YOU typed is left alone —
            it is not markdown, and asterisks in it are asterisks. */}
        {message.content ? (
          isHuman ? (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          ) : (
            <ChatMarkdown text={message.content} />
          )
        ) : (
          <div
            className={`text-xs italic ${isHuman ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}
          >
            (no content)
          </div>
        )}
        {message.has_tool_call && !isHuman && (
          <div className="text-[10px] text-indigo-700 dark:text-indigo-300 italic mt-1.5">
            ↻ updated suggestions
          </div>
        )}
      </div>
    </div>
  );
}


// Static quick-prompt fallback, picked by where the user is in the flow.
// Used only when the agent hasn't proposed its own follow-ups for the
// latest turn (see KnowledgePlanResponse.suggested_prompts).
function staticKnowledgePrompts(
  messageCount: number,
  suggestionCount: number,
  scopeName?: string,
): string[] {
  const where = scopeName ? `for ${scopeName}` : 'for this scope';
  if (messageCount === 0) {
    // Cold start — the blank-prompt "where do I begin?" moment.
    return [
      "I'm not sure where to start - what bedrock knowledge does this scope need?",
      `Propose 10 heuristics a writer should follow ${where}`,
      'What common misconceptions should we flag?',
      'What does an expert know that a novice misses here?',
      `What terminology should writers get right ${where}?`,
      'What contrarian or contested takes are worth noting?',
      'What facts or numbers should every page get right?',
      'Paste a URL and mine it for knowledge',
      'What relationships between concepts matter here?',
    ];
  }
  if (suggestionCount > 0) {
    // There's a working set — expand / sharpen / fill gaps.
    return [
      'Add a few expert-voice and contrarian takes',
      'What important knowledge types are we missing?',
      'Tighten any suggestions that are too generic',
      'Add some misconceptions readers commonly have',
      'Add key terminology writers should use',
      'Which of these should be promoted to the category?',
      'Add a few hard facts or specs',
      'Trim anything that overlaps or duplicates',
      'What would a skeptic push back on?',
    ];
  }
  // Chat has history but nothing in the working set yet.
  return [
    `Propose some heuristics and key facts ${where}`,
    'What terminology should writers get right?',
    'What misconceptions trip people up here?',
    'What does an expert know that a novice misses?',
    'What contrarian takes are worth noting?',
    'Paste a URL and mine it for knowledge',
    'What relationships between concepts matter?',
    `What's non-obvious but important ${where}?`,
    'What facts should every page get right?',
  ];
}

function formatChatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}


function hostname(u: string): string {
  try {
    return new URL(u).hostname;
  } catch {
    return u.slice(0, 40);
  }
}
